import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Platform } from 'react-native';

import { useOnboardingFinished } from '@hooks/useOnboardingActive';
import { MIXPANEL_EVENTS } from '@src/constants/constants';
import { useMixpanel } from '@src/utils/mixpanel';
import { AsyncStorage } from '@utils/storage';

const STORAGE_KEY_PROJECT_UUID = 'inviteMemberNudgeProjectUUId';
const STORAGE_KEY_DISMISS_COUNT = 'inviteMemberNudgeDismissCount';
const STORAGE_KEY_LAST_DISMISS_AT = 'inviteMemberNudgeLastDismissAt';
const STORAGE_KEY_COMPLETED = 'inviteMemberNudgeCompleted';

// Retry delays after each "Later": 24h, 48h
const RETRY_DELAYS_MS = [24 * 60 * 60 * 1000, 48 * 60 * 60 * 1000];
const MAX_DISMISSES = 2;

const isNative = Platform.OS !== 'web';

type InviteMemberNudgeContextType = {
  isVisible: boolean;
  projectUUId: string | null;
  activateNudge: (uuid: string) => Promise<void>;
  dismissNudge: () => Promise<void>;
  completeNudge: () => Promise<void>;
};

const InviteMemberNudgeContext = createContext<
  InviteMemberNudgeContextType | undefined
>(undefined);

export const InviteMemberNudgeProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { trackEvent } = useMixpanel();
  // TA-10920 #19: Smart Nudges only surface once onboarding has finished.
  const onboardingFinished = useOnboardingFinished();
  const [projectUUId, setProjectUUId] = useState<string | null>(null);
  const [dismissCount, setDismissCount] = useState(0);
  const [lastDismissAt, setLastDismissAt] = useState<number | null>(null);
  const [completed, setCompleted] = useState(false);
  const [loaded, setLoaded] = useState(false);
  // Tracks current time — updated when a retry window opens
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!isNative) {
      setLoaded(true);
      return;
    }
    const loadState = async () => {
      const [storedUUId, storedCount, storedLastAt, storedCompleted] =
        await Promise.all([
          AsyncStorage.getItem(STORAGE_KEY_PROJECT_UUID),
          AsyncStorage.getItem(STORAGE_KEY_DISMISS_COUNT),
          AsyncStorage.getItem(STORAGE_KEY_LAST_DISMISS_AT),
          AsyncStorage.getItem(STORAGE_KEY_COMPLETED),
        ]);
      if (storedUUId) setProjectUUId(storedUUId);
      if (storedCount) setDismissCount(parseInt(storedCount, 10) || 0);
      if (storedLastAt) setLastDismissAt(parseInt(storedLastAt, 10) || null);
      if (storedCompleted === 'true') setCompleted(true);
      setLoaded(true);
    };
    loadState();
  }, []);

  // Schedule a re-render exactly when the next retry window opens
  useEffect(() => {
    if (
      !projectUUId ||
      completed ||
      dismissCount === 0 ||
      dismissCount > MAX_DISMISSES ||
      !lastDismissAt
    )
      return;
    const delay = RETRY_DELAYS_MS[dismissCount - 1];
    const msUntilShow = lastDismissAt + delay - Date.now();
    if (msUntilShow <= 0) {
      setNow(Date.now());
      return;
    }
    const t = setTimeout(() => setNow(Date.now()), msUntilShow + 100);
    return () => clearTimeout(t);
  }, [projectUUId, completed, dismissCount, lastDismissAt]);

  const isVisible = useMemo(() => {
    if (!onboardingFinished) return false;
    if (!loaded || !projectUUId || completed) return false;
    if (dismissCount === 0) return true;
    if (dismissCount > MAX_DISMISSES) return false;
    if (!lastDismissAt) return true;
    const delay = RETRY_DELAYS_MS[dismissCount - 1];
    return now - lastDismissAt >= delay;
  }, [
    onboardingFinished,
    loaded,
    projectUUId,
    completed,
    dismissCount,
    lastDismissAt,
    now,
  ]);

  const activateNudge = useCallback(async (uuid: string) => {
    if (!isNative) return;
    await Promise.all([
      AsyncStorage.setItem(STORAGE_KEY_PROJECT_UUID, uuid),
      AsyncStorage.deleteItem(STORAGE_KEY_DISMISS_COUNT),
      AsyncStorage.deleteItem(STORAGE_KEY_LAST_DISMISS_AT),
      AsyncStorage.deleteItem(STORAGE_KEY_COMPLETED),
    ]);
    setProjectUUId(uuid);
    setDismissCount(0);
    setLastDismissAt(null);
    setCompleted(false);
    setNow(Date.now());
  }, []);

  const dismissNudge = useCallback(async () => {
    if (!isNative) return;
    const next = dismissCount + 1;
    const ts = Date.now();
    setDismissCount(next);
    setLastDismissAt(ts);
    setNow(ts);
    await Promise.all([
      AsyncStorage.setItem(STORAGE_KEY_DISMISS_COUNT, String(next)),
      AsyncStorage.setItem(STORAGE_KEY_LAST_DISMISS_AT, String(ts)),
    ]);
    trackEvent(MIXPANEL_EVENTS.INVITE_MEMBER_NUDGE_DISMISSED, {
      project_uuid: projectUUId,
      dismiss_count: next,
    });
  }, [dismissCount, projectUUId, trackEvent]);

  const completeNudge = useCallback(async () => {
    if (!isNative) return;
    if (completed) return;
    setCompleted(true);
    await AsyncStorage.setItem(STORAGE_KEY_COMPLETED, 'true');
    trackEvent(MIXPANEL_EVENTS.INVITE_MEMBER_NUDGE_COMPLETED, {
      project_uuid: projectUUId,
    });
  }, [completed, projectUUId, trackEvent]);

  const value = useMemo<InviteMemberNudgeContextType>(
    () => ({
      isVisible,
      projectUUId,
      activateNudge,
      dismissNudge,
      completeNudge,
    }),
    [isVisible, projectUUId, activateNudge, dismissNudge, completeNudge]
  );

  return (
    <InviteMemberNudgeContext.Provider value={value}>
      {children}
    </InviteMemberNudgeContext.Provider>
  );
};

export function useInviteMemberNudge(): InviteMemberNudgeContextType {
  const ctx = useContext(InviteMemberNudgeContext);
  if (!ctx) {
    throw new Error(
      'useInviteMemberNudge must be used within InviteMemberNudgeProvider'
    );
  }
  return ctx;
}
